import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderTree, Plus, Edit, Trash2, X, FolderPlus, ChevronRight, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import categoryService from '../../services/categoryService';

const CategoriesPage = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateSubModal, setShowCreateSubModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const queryClient = useQueryClient();

  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryService.getCategories()
  });

  const createMutation = useMutation({
    mutationFn: categoryService.createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setShowCreateModal(false);
      setShowCreateSubModal(false);
      setSelectedParent(null);
      toast.success('Category created successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => categoryService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setEditingCategory(null);
      toast.success('Category updated successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => categoryService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      toast.success('Category deleted successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  const toggleStatusMutation = useMutation({
    mutationFn: categoryService.toggleStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      toast.success('Category status updated');
    },
    onError: (error) => toast.error(error.message)
  });

  const categories = categoriesData?.data || [];

  const handleSubmit = (formData) => {
    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleCreateSubcategory = (parentCategory) => {
    setSelectedParent(parentCategory);
    setShowCreateSubModal(true);
  };

  const toggleExpanded = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const renderCategory = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);

    return (
      <React.Fragment key={category.id}>
        <tr className="hover:bg-gray-50">
          <td className="px-6 py-4 whitespace-nowrap">
            <div style={{ paddingLeft: `${level * 20}px` }} className="flex items-center">
              {hasChildren && (
                <button
                  onClick={() => toggleExpanded(category.id)}
                  className="mr-2 p-1 hover:bg-gray-200 rounded"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              )}
              {!hasChildren && <div className="w-6 mr-2" />}
              <div>
                <div className="text-sm font-medium text-gray-900 flex items-center">
                  <FolderTree className="w-4 h-4 mr-2 text-gray-400" />
                  {category.name}
                  {level > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                      Sub
                    </span>
                  )}
                </div>
                {category.description && (
                  <div className="text-sm text-gray-500">{category.description}</div>
                )}
              </div>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
              Level {category.level || 0}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <button
              onClick={() => toggleStatusMutation.mutate(category.id)}
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                category.is_active 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {category.is_active ? 'Active' : 'Inactive'}
            </button>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleCreateSubcategory(category)}
                className="text-green-600 hover:text-green-900 flex items-center text-xs bg-green-50 px-2 py-1 rounded"
                title="Add subcategory"
              >
                <FolderPlus className="w-3 h-3 mr-1" />
                Sub
              </button>
              <button
                onClick={() => setEditingCategory(category)}
                className="text-blue-600 hover:text-blue-900"
                title="Edit category"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Delete category "${category.name}"? This will also delete all subcategories and may affect tickets.`)) {
                    deleteMutation.mutate(category.id);
                  }
                }}
                className="text-red-600 hover:text-red-900"
                title="Delete category"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </td>
        </tr>
        {hasChildren && isExpanded && 
          category.children.map(child => renderCategory(child, level + 1))
        }
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FolderTree className="w-8 h-8 mr-3 text-blue-600" />
            Categories Management
          </h1>
          <p className="mt-2 text-gray-600">
            Organize your tickets with categories and subcategories
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Main Category
          </button>
          <button
            onClick={() => setShowCreateSubModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
          >
            <FolderPlus className="w-4 h-4 mr-2" />
            Subcategory
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <FolderTree className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Categories</p>
              <p className="text-2xl font-bold text-gray-900">
                {categories.reduce((acc, cat) => acc + 1 + (cat.children?.length || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <FolderTree className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Main Categories</p>
              <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <FolderPlus className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Subcategories</p>
              <p className="text-2xl font-bold text-gray-900">
                {categories.reduce((acc, cat) => acc + (cat.children?.length || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading categories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center">
            <FolderTree className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No categories found. Create your first category to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name & Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map(category => renderCategory(category))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Main Category Modal */}
      {showCreateModal && (
        <CategoryModal
          category={null}
          categories={categories}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleSubmit}
          isLoading={createMutation.isLoading}
          title="Create Main Category"
          isMainCategory={true}
        />
      )}

      {/* Create Subcategory Modal */}
      {showCreateSubModal && (
        <CategoryModal
          category={null}
          categories={categories}
          selectedParent={selectedParent}
          onClose={() => {
            setShowCreateSubModal(false);
            setSelectedParent(null);
          }}
          onSubmit={handleSubmit}
          isLoading={createMutation.isLoading}
          title="Create Subcategory"
          isSubcategory={true}
        />
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <CategoryModal
          category={editingCategory}
          categories={categories}
          onClose={() => setEditingCategory(null)}
          onSubmit={handleSubmit}
          isLoading={updateMutation.isLoading}
          title="Edit Category"
        />
      )}
    </div>
  );
};

const CategoryModal = ({ 
  category, 
  categories, 
  selectedParent,
  onClose, 
  onSubmit, 
  isLoading, 
  title,
  isMainCategory = false,
  isSubcategory = false 
}) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    parent_id: category?.parent_id || selectedParent?.id || '',
    is_active: category?.is_active !== false
  });

  const [errors, setErrors] = useState({});
  const [selectedLevel1, setSelectedLevel1] = useState('');
  const [selectedLevel2, setSelectedLevel2] = useState('');

  // Initialize level selections when editing
  useEffect(() => {
    if (category?.parent_id) {
      const parent = categories.find(cat => cat.id === category.parent_id);
      if (parent) {
        if (parent.parent_id) {
          // This is a level 2 category (grandchild)
          setSelectedLevel1(parent.parent_id);
          setSelectedLevel2(parent.id);
        } else {
          // This is a level 1 category (child)
          setSelectedLevel1(parent.id);
        }
      }
    }
  }, [category, categories]);

  // Initialize from selectedParent prop
  useEffect(() => {
    if (selectedParent) {
      if (selectedParent.parent_id) {
        // Selected parent is level 1, we're creating level 2
        setSelectedLevel1(selectedParent.parent_id);
        setSelectedLevel2(selectedParent.id);
      } else {
        // Selected parent is level 0, we're creating level 1
        setSelectedLevel1(selectedParent.id);
      }
    }
  }, [selectedParent]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }
    
    if (isSubcategory && !formData.parent_id) {
      newErrors.parent_id = 'Parent category is required for subcategories';
    }
    
    if (isMainCategory && formData.parent_id) {
      newErrors.parent_id = 'Main categories cannot have a parent';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      ...formData,
      parent_id: isMainCategory ? null : (formData.parent_id || null)
    });
  };

  // Get level 0 categories (root/main categories)
  const getLevel0Categories = () => {
    return categories.filter(cat => {
      return !cat.parent_id && (!category || cat.id !== category.id);
    });
  };

  // Get level 1 categories (children of selected level 0)
  const getLevel1Categories = () => {
    if (!selectedLevel1) return [];
    
    const level0Category = categories.find(cat => cat.id == selectedLevel1);
    if (!level0Category || !level0Category.children) return [];
    
    return level0Category.children.filter(cat => {
      return !category || cat.id !== category.id;
    });
  };

  // Get level 2 categories (children of selected level 1)
  const getLevel2Categories = () => {
    if (!selectedLevel2) return [];
    
    // Find the level 1 category in the nested structure
    const level0Category = categories.find(cat => cat.id == selectedLevel1);
    if (!level0Category || !level0Category.children) return [];
    
    const level1Category = level0Category.children.find(cat => cat.id == selectedLevel2);
    if (!level1Category || !level1Category.children) return [];
    
    return level1Category.children.filter(cat => {
      return !category || cat.id !== category.id;
    });
  };

  // Handle level 1 selection change
  const handleLevel1Change = (value) => {
    setSelectedLevel1(value);
    setSelectedLevel2('');
    
    if (value) {
      setFormData({ ...formData, parent_id: value });
    } else {
      setFormData({ ...formData, parent_id: '' });
    }
  };

  // Handle level 2 selection change
  const handleLevel2Change = (value) => {
    setSelectedLevel2(value);
    
    if (value) {
      setFormData({ ...formData, parent_id: value });
    } else {
      setFormData({ ...formData, parent_id: selectedLevel1 });
    }
  };

  const level0Categories = getLevel0Categories();
  const level1Categories = getLevel1Categories();
  const level2Categories = getLevel2Categories();

  // Determine what level we're creating
  const getCreationLevel = () => {
    if (isMainCategory) return 0;
    if (selectedLevel2) return 3; // Creating level 3 (great-grandchild)
    if (selectedLevel1) return level1Categories.length > 0 && !selectedLevel2 ? 2 : 1;
    return 1;
  };

  const creationLevel = getCreationLevel();

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
              {!isMainCategory && (
                <p className="text-sm text-gray-500 mt-1">
                  Creating Level {creationLevel} category
                  {creationLevel === 3 && " (Maximum depth reached)"}
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: '' });
                }}
                className={`mt-1 block w-full border rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter category name"
                required
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Enter category description"
              />
            </div>

            {/* Multi-level Category Selection */}
            {!isMainCategory && (
              <div className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700">Category Hierarchy</h4>
                
                {/* Level 0 Selection (Main Category) */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    1. Select Main Category {isSubcategory && '*'}
                  </label>
                  <select
                    value={selectedLevel1}
                    onChange={(e) => handleLevel1Change(e.target.value)}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.parent_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required={isSubcategory}
                  >
                    <option value="">Select Main Category</option>
                    {level0Categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Level 1 Selection (Child Category) - only show if level 0 selected and has children */}
                {selectedLevel1 && level1Categories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      2. Select Subcategory (Optional)
                    </label>
                    <select
                      value={selectedLevel2}
                      onChange={(e) => handleLevel2Change(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Create under "{level0Categories.find(c => c.id == selectedLevel1)?.name}"</option>
                      {level1Categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Level 2 Selection (Grandchild Category) - only show if level 1 selected and has children */}
                {selectedLevel2 && level2Categories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      3. Select Sub-subcategory (Optional)
                    </label>
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          setFormData({ ...formData, parent_id: e.target.value });
                        }
                      }}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Create under "{level1Categories.find(c => c.id == selectedLevel2)?.name}"</option>
                      {level2Categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-yellow-600">
                      ⚠️ Creating at maximum depth level
                    </p>
                  </div>
                )}

                {/* Show current selection path */}
                {selectedLevel1 && (
                  <div className="bg-blue-50 p-3 rounded-md">
                    <p className="text-sm text-blue-800 font-medium">Creating under:</p>
                    <p className="text-sm text-blue-700">
                      {level0Categories.find(c => c.id == selectedLevel1)?.name}
                      {selectedLevel2 && (
                        <> → {level1Categories.find(c => c.id == selectedLevel2)?.name}</>
                      )}
                    </p>
                  </div>
                )}

                {errors.parent_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.parent_id}</p>
                )}
                
                {selectedParent && (
                  <p className="mt-1 text-sm text-blue-600">
                    Pre-selected parent: <strong>{selectedParent.name}</strong>
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
              />
              <label htmlFor="is_active" className="text-sm text-gray-900">
                Active Category
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : (category ? 'Update' : 'Create')} Category
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;